import React from 'react';

import { widgetsConfigurations } from 'config/widgetConfigurations/widgetsConfigurations';
import { WidgetView, WidgetViewModeUtils } from 'utilitys/reporting/widgetViewModeUtils';

import { useBaseTable } from 'hooks/useBaseTable';
import { useBaseWidget } from 'hooks/useBaseWidget';

import { ITopOverdueData } from 'api/queries/getDelayDriver.query';

import { ColumnsManager } from 'components/common/ColumnsManager/ColumnsManager';
import { ExportDropdown } from 'components/common/ExportDropdown/exportDropdown';
import { List } from 'components/common/ListWrapper/list';
import { InputOption } from 'components/common/MyCustomSelect/components/InputOption/InputOption';
import { ValueContainer } from 'components/common/MyCustomSelect/components/ValueContainer/ValueContainer';
import NoData from 'components/common/NoData/noData';
import { TableV8 } from 'components/common/Tables/TableV8/tableV8';
import { TableContainer } from 'components/common/Tables/components/TableContainer/TableContainer';
import { TooltipIcon } from 'components/common/TooltipIcon/tooltipIcon';
import { WidgetExistInBackend } from 'components/common/WidgetExistInBackend/WidgetExistInBackend';
import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';
import widgetWithTitleClasses from 'components/common/WidgetWithTitle/WidgetWithTitle.module.scss';

import { statusOptions } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/criticalAnalysisDelayDriversFilter';
import { morefilterItems } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/ProgramCriticalAnalysis/utils';
import classes from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/style/table.module.scss';
import { AddWidget } from 'components/Dashboards/Project/Components/CustomDashboard/AddRemoveWidget/addWidget';
import { IComponentProps } from 'components/Dashboards/Project/Components/CustomDashboard/utils/utils.type';
import { AddToReport } from 'components/Dashboards/Reporting/AddToReport/AddToReport';
import { ReportingVersionAndPills } from 'components/Dashboards/Reporting/ReportingVersionAndPills/ReportingVersionAndPills';
import { Narrative } from 'components/Dashboards/Reporting/WidgetNarrativeWrapper/Narrative';
import { PinboardPills } from 'components/Dashboards/Reporting/pinboardPills/PinboardPills';
import { ThreeDotsMenu } from 'components/Dashboards/Reporting/threeDotsMenu/ThreeDotsMenu';
import { Select } from 'components/DesignSystem/Select';
import { Switch } from 'components/DesignSystem/Switch';
import { useDelayedActivitiesLogic } from 'components/widgets/DelayedActivities/DelayedActivities.logic';
import { useDelayedActivitiesProvider } from 'components/widgets/DelayedActivities/DelayedActivities.provider';
import {
    TDelayTypeOptions,
    TOP_OVERDUE_OVERRUN_FILTERS,
    columnsV8,
    delayTypeOptions,
    initialState,
} from 'components/widgets/DelayedActivities/DelayedActivities.utils';
import classesGeneralWidget from 'components/widgets/Utils/Styles/widget.module.scss';

const { componentKey, id, endpoint } = widgetsConfigurations.topOverdueOverrunActivities;

export const DelayedActivities = ({
    projectId,
    contractId,
    latestVersionId,
    compareVersionId,
    globalFilterData,
    widgetId,
    localFilters,
    externalComponents = null,
    editNarrative = null,
    setSubTitleForExport,

    reportWidgetRef = null,
    widgetSelectedProjectAndContract = null,
    widgetViewMode = WidgetView.page,
    title = widgetsConfigurations.topOverdueOverrunActivities.title,
}: IComponentProps) => {
    // Table
    const { table } = useBaseTable({
        widgetId,
        localFilters,
        componentKey,
        initialState,
        columns: columnsV8,
        widgetViewMode,
    });

    // Provider
    const { ui, api } = useDelayedActivitiesProvider({
        componentKey,
        widgetId,
        contractId,
        projectId,
        latestVersionId,
        compareVersionId,
        globalFilterData,
        localFilters,
        table,
        widgetViewMode,
    });

    // Widget
    const { subTitle, route, editableTitle, setEditableTitle } = useBaseWidget({
        id,
        widgetId,
        projectId,
        contractId,
        latestVersionId,
        title,
        setSubTitleForExport,
        ui,
        reportWidgetRef,
        onAddWidgetCallback: table.setWidgetColumns,
        apiPayload: api.getTopOverrunActivities.payload,
        widgetEndpoint: endpoint,
    });

    // Logic
    const { logic } = useDelayedActivitiesLogic({
        ui,
        api,
        title,
        subTitle,
        table,
    });

    return (
        <WidgetExistInBackend componentKey={componentKey}>
            <WidgetWithTitle
                id={id}
                title={
                    <WidgetTitle
                        title={editableTitle}
                        setTitle={setEditableTitle}
                        widgetViewMode={widgetViewMode}
                        postfix={logic.counter}
                        showPostfixInEditMode={true}
                    />
                }
                className={WidgetViewModeUtils.isReportView(widgetViewMode) ? widgetWithTitleClasses.inReport : ''}
                titleFilters={[
                    <ReportingVersionAndPills
                        key={'ReportingVersionAndPills'}
                        selectedProjectAndContract={widgetSelectedProjectAndContract}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                    />,
                    <Narrative key={'narrative'} editNarrative={editNarrative} />,
                    <PinboardPills
                        key={'pinboardPills'}
                        pills={ui.pills}
                        widgetViewMode={widgetViewMode}
                        isInPinboard={ui.isInPinboard}
                    />,
                    !WidgetViewModeUtils.isReportOrPinboardView(widgetViewMode) && (
                        <div key={'filters'} className={classes.filters}>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Status</div>
                                <Select<ISelectOption<string>, true>
                                    value={ui.state.statusFilter}
                                    options={statusOptions}
                                    onChange={logic.handleStatusType}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    isMulti={true}
                                    placeholder={'Select Status...'}
                                    components={{
                                        Option: InputOption,
                                        ValueContainer: ValueContainer,
                                    }}
                                />
                            </div>
                            <div>
                                <Switch
                                    onChange={logic.handleCriticalPath}
                                    checked={ui.state.isCriticalPath}
                                    label={' Critical Path Only'}
                                    labelPlacement={'start'}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Delay Type</div>
                                <Select<ISelectOption<TDelayTypeOptions[]>>
                                    value={ui.state.delayTypeStatus}
                                    options={delayTypeOptions}
                                    onChange={logic.handleDelayType}
                                    placeholder={'Select Delay Type...'}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>More Filters</div>
                                <Select<ISelectOption<string>, true>
                                    value={ui.state.status}
                                    options={morefilterItems}
                                    onChange={logic.handleStatus}
                                    closeMenuOnSelect={false}
                                    hideSelectedOptions={false}
                                    isMulti={true}
                                    placeholder={'Select More Filters...'}
                                    components={{
                                        Option: InputOption,
                                        ValueContainer: ValueContainer,
                                    }}
                                />
                            </div>
                            <div className={classes.selectWrapper}>
                                <div className={classes.title}>Next:</div>
                                <List<string>
                                    onChange={logic.handleTimePeriodClick}
                                    options={TOP_OVERDUE_OVERRUN_FILTERS}
                                    value={ui.state.numOfMonths}
                                />
                            </div>
                        </div>
                    ),
                ]}
                titleComponents={[
                    externalComponents && <div key={'externalComponents'}>{externalComponents}</div>,
                    <AddWidget
                        key={'AddWidgetToPinboard'}
                        title={title}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        widgetId={widgetId}
                        route={route}
                        localFilters={ui.state}
                        shouldHide={true}
                        callBack={table.setWidgetColumns}
                        shouldUseNewIcon={true}
                    />,
                    <AddToReport
                        key={'addWidgetToReport'}
                        widgetId={widgetId}
                        componentKey={componentKey}
                        projectId={projectId}
                        contractId={contractId}
                        uiState={ui.state}
                        shouldHide={WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode)}
                        callBack={table.setWidgetColumns}
                        apiPayload={api.getTopOverrunActivities.payload}
                        widgetEndpoint={endpoint}
                    />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ExportDropdown
                            key={'export'}
                            title={title}
                            subTitle={subTitle}
                            handleAPICsvExport={logic.handleAPICsvExport}
                        />
                    ),
                    !WidgetViewModeUtils.isReportView(widgetViewMode) && (
                        <ColumnsManager
                            onOpenModal={() => table.setModalColumnsOpen(true)}
                            onHideModal={() => table.setModalColumnsOpen(false)}
                            showModal={table.modalColumnsOpen}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            tableInstance={table.tableInstance}
                            initialState={initialState}
                            columns={columnsV8}
                            onChangeOrder={table.setColumnOrder}
                            onChangeVisibility={table.setColumnVisibility}
                            widgetViewMode={widgetViewMode}
                        />
                    ),
                    <TooltipIcon key={'info'} tooltip={api.getTopOverrunActivities.instance.data?.info} />,
                    !WidgetViewModeUtils.isReportAddOrEditWidget(widgetViewMode) && (
                        <ThreeDotsMenu
                            widgetViewMode={widgetViewMode}
                            widgetId={widgetId}
                            componentKey={componentKey}
                            shareTitle={title}
                            shareLink={route}
                            projectId={projectId}
                            contractId={contractId}
                        />
                    ),
                ]}
            >
                <TableContainer>
                    {table.updatedInitialState && (
                        <TableV8<ITopOverdueData>
                            data={logic.filteredRows}
                            columns={columnsV8}
                            isLoading={
                                api.getTopOverrunActivities.instance.isLoading ||
                                api.getTopOverrunActivities.instance.isFetching
                            }
                            noData={
                                <NoData>
                                    {logic.notApplicable
                                        ? 'Not applicable with applied filter'
                                        : 'No activities match the applied filter'}
                                </NoData>
                            }
                            sorting={table.sorting}
                            setSorting={table.setSorting}
                            columnVisibility={table.columnVisibility}
                            setColumnVisibility={table.setColumnVisibility}
                            columnOrder={table.columnOrder}
                            setColumnOrder={table.setColumnOrder}
                            rowSelection={table.rowSelection}
                            setRowSelection={table.setRowSelection}
                            tableClassName={
                                WidgetViewModeUtils.isReportView(widgetViewMode)
                                    ? classesGeneralWidget.reportTableScrollable
                                    : classesGeneralWidget.pageTableScrollable
                            }
                            wrapperClassName={
                                WidgetViewModeUtils.isInAnyReportMode(widgetViewMode)
                                    ? classesGeneralWidget.reportTableWrapper
                                    : ''
                            }
                            pagination={table.pagination}
                            totalPages={table.totalPages}
                            setPagination={table.setPagination}
                            isPaginationDisabled={api.getTopOverrunActivities.instance.isFetching}
                            displayPaginationBottomBar
                        />
                    )}
                </TableContainer>
            </WidgetWithTitle>
        </WidgetExistInBackend>
    );
};
